import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getProjects, createProject, updateProject, deleteProject, getProjectDeletionImpact, safeErrorMessage } from '../services/api';
import { HiOutlineFolder, HiOutlinePlus, HiOutlineSearch, HiOutlineTrash, HiOutlineEye, HiOutlinePencil, HiOutlineUser } from 'react-icons/hi';
import { FiGrid } from 'react-icons/fi';
import Modal from '../components/Modal';
import DeleteImpactModal from '../components/DeleteImpactModal';
import { useToast } from '../App';
import { useAuth } from '../contexts/AuthContext';

export default function ProjectsPage({ onProjectsChange }) {
  const { isAdmin } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', code: '', description: '' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editProject, setEditProject] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', code: '', description: '' });
  const addToast = useToast();


  const loadProjects = async () => {
    setLoading(true);
    const { data } = await getProjects();
    setProjects(data);
    setLoading(false);
  };

  useEffect(() => { loadProjects(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    await createProject(form);
    setForm({ name: '', code: '', description: '' });
    setShowModal(false);
    loadProjects();
    if (onProjectsChange) onProjectsChange();
    addToast('success', 'Le projet a été créé avec succès.', 'Projet créé');
  };

  const handleOpenEdit = (e, p) => {
    e.stopPropagation();
    setEditProject(p);
    setEditForm({ name: p.name, code: p.code, description: p.description || '' });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await updateProject(editProject.id, editForm);
      setEditProject(null);
      loadProjects();
      if (onProjectsChange) onProjectsChange();
      addToast('success', 'Le projet a été modifié avec succès.', 'Projet modifié');
    } catch {
      addToast('error', 'Erreur lors de la modification du projet.');
    }
  };

  const handleDelete = async (impact) => {
    if (!confirmDelete) return;
    try {
      const { data } = await deleteProject(confirmDelete.id);
      const detail = (data?.impact || impact?.consequences || []).join(' ');
      setConfirmDelete(null);
      loadProjects();
      if (onProjectsChange) onProjectsChange();
      addToast(
        'success',
        detail
          ? `Projet « ${confirmDelete.name} » supprimé. ${detail}`
          : `Projet « ${confirmDelete.name} » supprimé.`,
        'Suppression effectuée',
      );
    } catch (err) {
      // Le serveur explique son refus : on le rapporte tel quel
      addToast('error', safeErrorMessage(err, 'Suppression impossible'), 'Suppression refusée');
    }
  };

  const filtered = projects.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.code.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="loading-spinner">
      <output className="spinner-border"></output>
      <span>Chargement...</span>
    </div>
  );

  return (
    <div>
      <div className="page-header">
        <div>
          <h2><HiOutlineFolder className="me-2" />Projets</h2>
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>{projects.length} projet{projects.length > 1 ? 's' : ''}</p>
        </div>
        {isAdmin && (
          <div className="actions">
            <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>
              <HiOutlinePlus /> Nouveau Projet
            </button>
          </div>
        )}
      </div>
      <div className="mb-4">
        <div className="position-relative" style={{ maxWidth: '400px' }}>
          <HiOutlineSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)', fontSize: '1rem' }} />
          <input className="form-control" placeholder="Rechercher un projet..." value={search}
                 onChange={(e) => setSearch(e.target.value)} style={{ paddingLeft: '36px' }} />
        </div>
      </div>

      <div className="row g-3">
        {filtered.map((p) => (
          <div key={p.id} className="col-md-4">
            <div className="card project-card h-100" style={{ cursor: 'pointer', position: 'relative' }}>
              <div className="card-body">
                <div className="d-flex align-items-start justify-content-between mb-2">
                  <h5 className="card-title mb-0">
                    <Link to={`/projects/${p.id}`} className="text-decoration-none text-navy stretched-link">
                      {p.name}
                    </Link>
                  </h5>
                  <span className="project-code">{p.code}</span>
                </div>
                {p.description && <p className="text-muted small mb-2">{p.description}</p>}
                {p.owner_username && (
                  <div className="text-muted d-flex align-items-center gap-1 mb-2" style={{ fontSize: '0.72rem' }}>
                    <HiOutlineUser style={{ flexShrink: 0 }} />
                    <span>Créé par <strong>{p.owner_full_name || p.owner_username}</strong></span>
                  </div>
                )}
                <div className="project-stats">
                  <div className="project-stat">
                    <div className="project-stat-value">{p.requirements_count || 0}</div>
                    <div className="project-stat-label">Exigences</div>
                  </div>
                  <div className="project-stat">
                    <div className="project-stat-value">{p.test_cases_count || 0}</div>
                    <div className="project-stat-label">Cas de Test</div>
                  </div>
                </div>
              </div>
              <div className="card-footer d-flex gap-2" style={{ position: 'relative', zIndex: 1 }}>
                <Link to={`/projects/${p.id}`} className="btn btn-outline-primary btn-sm"><HiOutlineFolder /> Ouvrir</Link>
                <Link to={`/projects/${p.id}/preview`} className="btn btn-outline-info btn-sm"><HiOutlineEye /> Preview</Link>
                {isAdmin && <button className="btn btn-outline-secondary btn-sm" onClick={(e) => handleOpenEdit(e, p)} title="Modifier le projet"><HiOutlinePencil /></button>}
                {isAdmin && <button className="btn btn-outline-danger btn-sm ms-auto" onClick={() => setConfirmDelete(p)} title="Supprimer le projet"><HiOutlineTrash /></button>}
              </div>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon"><FiGrid /></div>
          <p>{search ? 'Aucun projet trouvé.' : isAdmin ? 'Aucun projet. Créez-en un nouveau.' : 'Aucun projet assigné.'}</p>
          {!search && isAdmin && (
            <button className="btn btn-primary btn-sm mt-3" onClick={() => setShowModal(true)}>
              <HiOutlinePlus /> Nouveau Projet
            </button>
          )}
        </div>
      )}
      <Modal show={showModal} onClose={() => setShowModal(false)} title="Nouveau Projet" icon={<HiOutlinePlus />}>
        <form onSubmit={handleCreate}>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-project-name">Nom du projet</label>
            <input id="field-project-name" className="form-control" placeholder="Ex: Delta-Bank - Retrait Espèces"
                   value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required autoFocus />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-project-code">Code</label>
            <input id="field-project-code" className="form-control" placeholder="Ex: guretesp"
                   value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-project-description">Description</label>
            <textarea id="field-project-description" className="form-control" rows="2" placeholder="Description du projet"
                   value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setShowModal(false)}>Annuler</button>
            <button type="submit" className="btn btn-primary btn-sm">Créer le projet</button>
          </div>
        </form>
      </Modal>

      <Modal show={!!editProject} onClose={() => setEditProject(null)} title="Modifier le projet" icon={<HiOutlinePencil />}>
        <form onSubmit={handleUpdate}>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-edit-project-name">Nom du projet</label>
            <input id="field-edit-project-name" className="form-control" placeholder="Ex: Delta-Bank - Retrait Espèces"
                   value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required autoFocus maxLength={200} />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-edit-project-code">Code</label>
            <input id="field-edit-project-code" className="form-control" placeholder="Ex: guretesp"
                   value={editForm.code} onChange={(e) => setEditForm({ ...editForm, code: e.target.value })} required maxLength={50} />
          </div>
          <div className="mb-3">
            <label className="form-label" htmlFor="field-edit-project-description">Description</label>
            <textarea id="field-edit-project-description" className="form-control" rows="2" placeholder="Description du projet"
                   value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} />
          </div>
          <div className="d-flex gap-2 justify-content-end">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setEditProject(null)}>Annuler</button>
            <button type="submit" className="btn btn-primary btn-sm">Enregistrer</button>
          </div>
        </form>
      </Modal>

      <DeleteImpactModal
        show={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer le projet"
        targetLabel={confirmDelete ? `${confirmDelete.name} (${confirmDelete.code})` : ''}
        fetchImpact={() => getProjectDeletionImpact(confirmDelete.id)}
        confirmText="Supprimer le projet"
      />
    </div>
  );
}
